import { countedKey } from "@/lib/i18n/plural";
import { useMemo, useState } from "react";
import {
  BasisDisclosure,
  BasisDoorButton,
  Button,
  Checkbox,
  Combobox,
  FormField,
  Modal,
  TextInput,
  type BasisSection,
} from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { DrawerWarning } from "./DrawerWarning";
import { ReturnToStockChoice } from "./ReturnToStockChoice";
import { useMoney } from "@/shared/ui";
import {
  addonLabelForLocale,
  comboLabelForLocale,
  plateNameForLocale,
  substitutionLabelForLocale,
} from "@/modules/pos/lib/groupPlates";
import {
  DEFAULT_REFUND_LATE_AFTER_MINUTES,
  allocateInvoiceRefundShares,
  isRefundLate,
  pickableRefundItems,
  refundableItems,
  sumRefundShares,
  thresholdInHours,
} from "@/modules/pos/lib/refundAllocation";
import { allocationBasis } from "@/modules/pos/lib/partyShares";
import {
  VOID_REASON_NOTE_MAX_LENGTH,
  canConfirmVoid,
  canConfirmVoidReason,
  isVoidReasonNoteRequired,
  nextReasonCodeForQuery,
  reasonCodesForKind,
  returnableItemIds,
  voidReasonLabel,
  type ReasonCode,
} from "@/modules/pos/lib/voidReasons";
import { refundMethodCopy } from "@/modules/pos/lib/refundMethodCopy";
import { roundMoney } from "@/modules/pos/lib/pricing";
import type { Invoice, InvoiceItem } from "@/lib/api/types";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";

/**
 * Giving money back (R1–R13), at the till.
 *
 * This is the one screen in the product where money that has already been taken
 * leaves again, so every rule that decides the number is ON THE SHEET rather
 * than in a comment — trap 1, which this codebase has already paid for once: a
 * tier that "applies on top of an override" lived only in a comment, a manager
 * typed 200 during a happy hour, and the guest paid 160.
 *
 * What the cashier can read here, and can therefore predict:
 *
 * - **Plates, never an amount** (R2). There is no amount field, here or on the
 *   wire: `refundInvoice`'s payload carries `itemIds` and nothing else that is
 *   money, and the server derives every figure. The sheet quotes what each
 *   plate is worth so the cashier knows before confirming, not after.
 * - **The service charge and VAT come back with it** (R3), in proportion, per
 *   plate — printed on the plate's own row, because the residue is real: two
 *   plates that cost the same can carry VAT shares a piastre apart, and a
 *   customer holding the refund receipt will ask.
 * - **The method is LOCKED** (R6). The money goes back the way it came: cash
 *   out of this drawer, card back to the card, an InstaPay transfer back by
 *   transfer out of the bank (D44). It is shown, disabled, with the reason
 *   beside it — a cash refund on a card sale would leave the drawer permanently
 *   short and destroy the variance signal the drawer exists for, and a cash
 *   refund on a transfer would do the same for money that was never in the till.
 *   The sentence beside it names the DRAWER CONSEQUENCE and not just the
 *   method, because that is what the cashier has to predict at close: cash
 *   moves `expectedCash`, card moves `expectedCard`, a transfer moves neither.
 * - **LATE is a warning, never a refusal** (R8). Past the threshold the sheet
 *   says so, prominently, and Confirm stays enabled. Refusing would not stop
 *   the money going back; it would only push it out of the product as an
 *   untracked cash payout.
 * - **The cost stays unless returned to stock** (R13, amended by D141). The
 *   food was cooked. Revenue leaves, cost does not, so the margin drops — said
 *   in words here, because it is otherwise reported as a bug the first time a
 *   manager reads the item report. The one exception is the cashier's to make:
 *   when a ticked plate was cooked (PREPARING/READY/SERVED — the till's
 *   knowledge; the server re-checks the ledger) the sheet asks where the food
 *   went, ONE choice for every cooked plate ticked, default *wasted*. "Returned
 *   to stock" puts the ingredients back at their original cost and takes that
 *   cost off, on the refund's own business day.
 * - **It lands in the drawer that is open now** (R7), not the one the bill was
 *   settled in, whose snapshot was counted and signed and must not be restated.
 * - **A plate that already came back is shown as such and cannot be picked.**
 *   `InvoiceItem.refunded` is DERIVED by the server from the money ledger, so
 *   the sheet no longer has to offer the money a second time and apologise
 *   afterwards. It does not replace the typed refusal — the server still
 *   answers `RESTAURANT_ITEM_ALREADY_REFUNDED`, and must, because two tills can
 *   open this sheet on the same bill at once.
 */
export function RefundSheet({
  invoice,
  isSubmitting,
  lateAfterMinutes = DEFAULT_REFUND_LATE_AFTER_MINUTES,
  warning,
  onOpenDrawer,
  onCancel,
  onConfirm,
}: {
  invoice: Invoice;
  isSubmitting: boolean;
  /**
   * R8's threshold, in minutes.
   *
   * The STORE's own value, from `GET /restaurant/settings`. Defaulted, not
   * required, so the sheet still renders while that read is in flight or has
   * failed — and the default IS the server's own constant, so a sheet that
   * falls back agrees with the flag the ledger will store rather than warning
   * about a threshold nobody configured.
   */
  lateAfterMinutes?: number;
  /**
   * The D25 pre-warning, exactly as the settle sheet takes it: with shift
   * tracking on and no drawer open the server refuses this refund as
   * `RESTAURANT_SHIFT_REQUIRED`. Money is physically leaving a till, so it must
   * leave a drawer that is open.
   */
  warning?: string;
  /**
   * FIX IT WITHOUT LEAVING THIS SHEET (Unit C) — opens the drawer dialog over it.
   *
   * This sheet used to state the refusal and stop there, which left a cashier
   * holding a customer's money and no move that keeps the sale on the system.
   * Omitted for anyone without the drawer rung: their sentence names a person to
   * ask, and a button whose only outcome is a refusal teaches them to stop reading
   * the warning.
   */
  onOpenDrawer?: () => void;
  onCancel: () => void;
  /**
   * `refundDeliveryFee` is the cashier's explicit per-refund choice (F17), sent
   * as its own flag and never inferred from the plate selection — so "they ticked
   * every plate" is not read as "they meant the fee too".
   */
  /**
   * `returnToStockItemIds` (D141) is EMPTY unless the cashier chose "returned
   * to stock" — then it is the ticked plates the till knows were cooked.
   */
  onConfirm: (
    itemIds: string[],
    reasonCode: string,
    reasonNote: string,
    refundDeliveryFee: boolean,
    returnToStockItemIds: string[],
  ) => void | Promise<void>;
}) {
  const t = useTranslate();
  const money = useMoney();
  /*
   * The sheet reads plates in the language the sheet is IN.
   *
   * Every other till label is Arabic-only on purpose — the till is staffed by
   * Arabic readers. This sheet is the exception the refund receipt already
   * makes: it prints both names, because it is read at the moment money is
   * handed over and the plate has to be identifiable to whoever is reading.
   * An all-English sheet listing «مشروب غازي» failed exactly there.
   */
  const { locale } = useI18n();
  const plateName = (item: InvoiceItem) => plateNameForLocale(item, locale);

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  // DEF-7: nothing pre-selected. A pre-filled picker reads as "already
  // answered" and the stale value goes out on the command log.
  const [reasonCode, setReasonCode] = useState<ReasonCode | null>(null);
  const [reasonNote, setReasonNote] = useState("");
  /*
   * F17 — the courier's fee, as its own tick.
   *
   * NOT pre-selected, for DEF-7's reason above and a sharper one: this is the
   * founder's "the cashier's choice, per refund" made a control. A box that
   * arrives ticked would hand the fee back on every refund by default, which is
   * the opposite of a choice.
   */
  const [feeSelected, setFeeSelected] = useState(false);
  const noteRequired = isVoidReasonNoteRequired(reasonCode);

  /*
   * The WHOLE chargeable set goes into the allocation, never the subset being
   * refunded — the policy's property 4. Passing the ticked plates alone would
   * inflate every weight and quote a refund larger than the bill.
   */
  const plates = useMemo(() => refundableItems(invoice), [invoice]);
  const shares = useMemo(
    () =>
      allocateInvoiceRefundShares(
        // D146: pro-rated onto a discounted bill's food base.
        allocationBasis(invoice),
        plates.map((item) => ({ id: item.id, lineTotal: item.lineTotal })),
      ),
    [invoice, plates],
  );

  /*
   * The allocation basis stays the WHOLE chargeable set (property 4), so
   * `plates` above is NOT filtered — a plate that already came back is still
   * part of what the shares were computed over, and removing it would inflate
   * every remaining plate's share. Only the PICKABLE set narrows, and the rule
   * lives in `refundAllocation.ts` beside the basis it must not become.
   */
  const pickable = useMemo(() => pickableRefundItems(invoice), [invoice]);

  const selectedIds = pickable
    .map((item) => item.id)
    .filter((id) => selected.has(id));
  const selectedTotal = shares
    ? sumRefundShares(shares, selectedIds)
    : null;

  const allSelected =
    pickable.length > 0 && selectedIds.length === pickable.length;

  /*
   * D141 — the ticked plates that were cooked, and the cashier's one answer
   * for all of them. Default wasted: an untouched sheet sends the refund it
   * sent before D141. The choice is shown only while a cooked plate is ticked,
   * and is reported only while it is shown — unticking the last cooked plate
   * cannot leave a "returned" answer armed behind a hidden control.
   */
  const [returned, setReturned] = useState(false);
  const returnableSelectedIds = returnableItemIds(
    pickable.filter((item) => selected.has(item.id)),
  );
  const showReturnChoice = returnableSelectedIds.length > 0;

  // R8, decided on the same rule the server decides it on. `settledAt` is
  // non-null on any SETTLED bill, which is the only status this sheet opens on.
  const late = invoice.settledAt
    ? isRefundLate(new Date(invoice.settledAt), new Date(), lateAfterMinutes)
    : false;
  const lateHours = thresholdInHours(lateAfterMinutes);

  /*
   * R6 — the method, and the WORDS that go with it (trap 1: the rule is on the
   * sheet, not in a comment). Three methods can reach this sheet
   * (`REFUNDABLE_SETTLEMENT_METHODS`) and each has a DIFFERENT drawer
   * consequence, so each earns its own sentence: cash leaves this drawer, a
   * card credit leaves the terminal, a transfer leaves the bank and touches
   * neither. A cashier who cannot read which one this is cannot predict their
   * own count at close.
   */
  const method = invoice.settlementMethod;
  const copy = refundMethodCopy(method);
  const methodLabel = t(copy.label);
  const methodSentence = t(copy.sentence);

  const handleReasonQueryChange = (query: string) => {
    setReasonCode((current) => nextReasonCodeForQuery(t, current, query));
  };

  const toggle = (itemId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  /*
   * F17 — is there a fee to offer at all, and is it still claimable?
   *
   * The fee AND the tax on it: they came in together on one line of the bill and
   * they go back together in one ledger row, so the sheet quotes the sum the
   * customer will actually receive rather than the pre-tax figure printed on the
   * bill's own fee line.
   */
  const feeAmount = roundMoney(invoice.deliveryFee + invoice.deliveryFeeVat);
  const feeOffered = feeAmount > 0;
  const feeAlreadyBack = invoice.deliveryFeeRefunded === true;
  const feePickable = feeOffered && !feeAlreadyBack;
  const feeBack = feePickable && feeSelected ? feeAmount : 0;

  /*
   * WHAT THE CUSTOMER ACTUALLY RECEIVES — plates plus the fee, in one figure.
   *
   * The headline used to be the plate allocation alone, which was exactly right
   * while the fee could not come back and would have been a lie the moment it
   * could: a fee-only refund would have greeted the cashier with 0.00 in grey
   * while Confirm handed over 34.20.
   */
  const backToCustomer = roundMoney((selectedTotal?.amount ?? 0) + feeBack);

  /*
   * EITHER a plate or the fee — the server's own rule
   * (`RESTAURANT_REFUND_NOTHING_REQUESTED`), mirrored here so Confirm is
   * disabled rather than the cashier meeting a 400. A fee-only refund is the
   * case this exists for: the food arrived and was eaten, the delivery is what
   * failed.
   */
  const canConfirm =
    (selectedIds.length > 0 || feeBack > 0) &&
    canConfirmVoid(reasonCode, reasonNote);

  /*
   * THE TILL'S DOOR (UX-round D22).
   * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
   *
   * Six rule sentences used to render unconditionally, in a bordered box at
   * 11.5px, between the plate picker and the reason field — roughly 700 Arabic
   * characters, about 220px of prose on a 390px phone, on EVERY refund. The
   * inventory measured this sheet at 72% explanation, and the code's own
   * comment named the reader it was written for: *"a manager must be able to
   * read this sheet and predict the number."* That reader is doing arithmetic
   * at a desk. The person holding the phone has a guest standing there.
   *
   * A DISCLOSURE, NOT A DELETION (charter invariant 6). Every clause is still
   * here, word for word, one tap away and in bigger type than it had on the
   * canvas. Nothing was cut, because several of these are genuinely
   * non-obvious — the refund lands in TONIGHT's drawer, not the bill's; the
   * cost stays on the books — and a cashier who needs one needs all of it.
   *
   * `BasisDoorButton` + `BasisDisclosure` rather than a local `<details>`: this
   * is the platform's "one array, three surfaces" pattern, already carrying
   * five report pages. `elevated`, because it opens OVER this sheet.
   *
   * WHAT STAYS ON THE CANVAS, and why exactly one clause does: `pos.refundRulePerPlateShort`
   * explains an ABSENCE — why there is no amount field to type into — and an
   * absence has no row to hide behind. A cashier hunting for a box that is not
   * there will not think to open a door to be told it never existed.
   */
  const [ruleDoorOpen, setRuleDoorOpen] = useState(false);
  const ruleSections: BasisSection[] = [
    {
      /* The SECTION heading is the surface the door was pressed on — the same
         rule the report pages follow, where a reader who pressed a door inside
         a card lands on that card's own title. The sheet's own header carries
         the question («المرتجع بيتحسب إزاي»); using it for both printed the
         same sentence twice, one under the other, which a browser pass found
         at 390px. */
      id: "refund-rules",
      title: t((m) => m.pos.refundTitle),
      pairs: [
        {
          term: t((m) => m.pos.refundRuleTermPlates),
          def: t((m) => m.pos.refundRulePerPlate),
        },
        {
          term: t((m) => m.pos.refundRuleTermCharges),
          def: t((m) => m.pos.refundRuleCharges),
        },
        {
          term: t((m) => m.pos.refundRuleTermMethod),
          def: t((m) => m.pos.refundRuleMethod),
        },
        {
          term: t((m) => m.pos.refundRuleTermDrawer),
          def: t((m) => m.pos.refundRuleDrawer),
        },
        {
          term: t((m) => m.pos.refundRuleTermBill),
          def: t((m) => m.pos.refundRuleBillUnchanged),
        },
        {
          term: t((m) => m.pos.refundRuleTermCost),
          def: t((m) => m.pos.refundRuleCost),
        },
      ],
    },
  ];

  /*
   * A refund in progress: a plate ticked, the fee ticked, a reason chosen or a
   * note typed. `ModalSheet` gives this sheet swipe-down-to-dismiss, and a
   * half-built refund thrown away by a thumb is the cashier starting again
   * with the customer watching.
   */
  const guard = useDiscardGuard(
    selectedIds.length > 0 ||
      feeSelected ||
      (showReturnChoice && returned) ||
      reasonCode !== null ||
      reasonNote.trim() !== "",
    onCancel,
  );

  return (
    <>
    <Modal
      isOpen
      onClose={guard.requestClose}
      size="sm"
      title={t((m) => m.pos.refundTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="refund-sheet"
      /*
       * The door sits in the header, ahead of the plate list in DOM order —
       * and carries NO `data-autofocus` (ruling F13's hard condition). The
       * list keeps both `data-autofocus` and `tabindex="-1"`, so focus still
       * lands on the decision and not on a door or on the reason combobox,
       * whose panel opens on focus and covers the plates.
       */
      headerTrailing={
        <BasisDoorButton
          label={t((m) => m.pos.refundRulesDoorLabel)}
          expanded={ruleDoorOpen}
          onClick={() => setRuleDoorOpen(true)}
          testId="refund-rules-door"
        />
      }
      footerActions={
        <>
          <Button variant="outline" size="touch" onClick={guard.requestClose}>
            {t((m) => m.common.cancel)}
          </Button>
          {/* Solid danger: this IS the primary destructive action of the
              sheet, and the one place a filled red belongs. In the PRIMITIVE's
              footer now, outside `overflow-y-auto` — a sheet that can carry
              twenty plates could otherwise put its own confirm below its own
              fold. */}
          <Button
            variant="danger"
            size="touch"
            className="font-extrabold"
            disabled={!canConfirm}
            isLoading={isSubmitting}
            data-testid="refund-confirm"
            onClick={() => {
              if (!canConfirm || reasonCode === null) return;
              void onConfirm(
                selectedIds,
                reasonCode,
                reasonNote.trim(),
                feeBack > 0,
                showReturnChoice && returned ? returnableSelectedIds : [],
              );
            }}
          >
            {t((m) => m.pos.refundConfirm)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* THE METHOD, LOCKED (R6). Shown as a read-only row rather than as a
            disabled toggle pair: a greyed-out second option invites the tap
            that asks "why can't I?", and the answer is a sentence, not a
            tooltip. */}
        <div
          className="flex flex-col gap-1 rounded-xl bg-[color:var(--surface-alt)] px-3 py-2.5"
          data-testid="refund-method-locked"
        >
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-bold text-[color:var(--text-muted)]">
              {t((m) => m.pos.refundMethodLabel)}
            </span>
            <span
              className="text-sm font-extrabold"
              data-testid="refund-method-value"
            >
              {methodLabel}
            </span>
          </div>
          <span className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold leading-relaxed text-[color:var(--text-secondary)]">
            {methodSentence}
          </span>
        </div>

        {/* B29 — this BILL's own charges exceed its own subtotal, so its tax
            split cannot be returned in full however the refund is allocated.
            Stated BEFORE the confirm, like every other warning in this block
            and like D81's method divergence: the guest's money is unaffected,
            nothing is refused, and the person holding the refund rung — no
            preset role below OWNER (D76) — decides whether to go ahead. `0` on
            every bill the product can price, so this never renders in practice;
            it exists for the day a snapshot disagrees with itself. */}
        {invoice.chargeSnapshotExcess > 0 ? (
          <p
            role="alert"
            className="rounded-xl bg-[color:var(--warning-100,var(--surface-alt))] px-3 py-2 text-xs font-bold leading-relaxed text-[color:var(--warning)]"
            data-testid="refund-charge-snapshot-warning"
          >
            {t((m) => m.pos.refundChargeSnapshotWarning, {
              amount: money(invoice.chargeSnapshotExcess),
            })}
          </p>
        ) : null}

        {/* R8 — late, and still allowed. The sentence says both halves: a
            warning that only said "late" would read as a refusal about to
            happen, and the cashier would go and find the untracked payout. */}
        {late ? (
          <p
            role="alert"
            className="rounded-xl bg-[color:var(--warning-100,var(--surface-alt))] px-3 py-2 text-xs font-bold leading-relaxed text-[color:var(--warning)]"
            data-testid="refund-late-warning"
          >
            {lateHours !== null
              ? t(
                  // «{hours} ساعة» is a counted noun: «ساعة واحدة» at one,
                  // «ساعتين» at two, «{hours} ساعات» at 3–10. The figure itself
                  // still goes in as a string, the way this file passes every
                  // number into a sentence.
                  countedKey(lateHours, {
                    one: (m) => m.pos.refundLateWarningHoursOne,
                    two: (m) => m.pos.refundLateWarningHoursTwo,
                    few: (m) => m.pos.refundLateWarningHoursFew,
                    many: (m) => m.pos.refundLateWarningHours,
                  }),
                  {
                    hours: String(lateHours),
                  },
                )
              : t((m) => m.pos.refundLateWarningMinutes, {
                  minutes: String(lateAfterMinutes),
                })}
            {/* ...AND THE CLAUSE THAT USED TO BE PASTED INSIDE EVERY ARM.

                Five copies of one sentence — four plural arms plus the minutes
                arm — is one edit done five times and four chances to drift. It
                is a sibling here, on the same sheet and inside the same alert,
                because it is not an explanation to be tucked behind the door:
                it is the half that says the refund WILL go through. A warning
                that only said "late" reads as a refusal about to happen, and
                the cashier goes and finds the untracked payout. */}{" "}
            <span data-testid="refund-late-recorded">
              {t((m) => m.pos.refundLateRecordedNote)}
            </span>
          </p>
        ) : null}

        {/* D25's pre-warning, in the sheet the cashier is looking at. */}
        {warning ? (
          <DrawerWarning
            message={warning}
            actionLabel={t((m) => m.pos.noShiftWarningOpen)}
            onOpenDrawer={onOpenDrawer}
            testId="refund-shift-warning"
          />
        ) : null}

        {/* Already handed back off this bill. Only when there is one — a "0.00
            given back" line on every ordinary refund would teach the cashier to
            stop reading this block. */}
        {invoice.refundTotal > 0 ? (
          <p
            className="rounded-xl bg-[color:var(--surface-alt)] px-3 py-2 text-xs font-semibold leading-relaxed text-[color:var(--text-secondary)]"
            data-testid="refund-already-note"
          >
            {t((m) => m.pos.refundAlreadyNote, {
              amount: money(invoice.refundTotal),
            })}
          </p>
        ) : null}

        {/* PLATES, NEVER AN AMOUNT (R2). */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-bold text-[color:var(--text-muted)]">
            {t((m) => m.pos.refundPickPlates)}
          </span>
          <Button
            variant="outline"
            size="sm"
            data-testid="refund-select-all"
            disabled={pickable.length === 0}
            onClick={() =>
              setSelected(
                allSelected
                  ? new Set()
                  : new Set(pickable.map((item) => item.id)),
              )
            }
          >
            {allSelected
              ? t((m) => m.pos.refundClearAll)
              : t((m) => m.pos.refundSelectAll)}
          </Button>
        </div>

        {/*
          ONE empty state, and it is a RACE GUARD — not a second opening case.

          The sheet cannot OPEN with nothing to tick: `isRefundOfferable` gates
          the control on `pickableRefundItems(invoice).length > 0`. What reaches
          here is the sheet already open when the bill's own read catches up with
          a refund made somewhere else — another till, or the back office — and
          takes the last plate away underneath it. The cashier then needs to be
          told the money is already out, which is what this sentence says.

          It used to carry a SECOND arm for `plates.length === 0` and its own
          string. That arm was unreachable twice over: `pickable` is a filter of
          `plates`, so an empty `plates` implies an empty `pickable`, and a bill
          with no chargeable plate at all never offers the control in the first
          place. An empty state a product cannot show is not a courtesy — it is
          a sentence nobody has ever read, kept in two catalogues, describing
          behaviour that does not exist.
        */}
        {pickable.length === 0 ? (
          <p
            className="rounded-xl bg-[color:var(--surface-alt)] px-3 py-3 text-xs font-semibold text-[color:var(--text-secondary)]"
            data-testid="refund-no-plates"
          >
            {t((m) => m.pos.refundAllPlatesBack)}
          </p>
        ) : (
          <ul
            /*
             * WHERE THE SHEET LANDS FOCUS, and it has to be said explicitly here.
             *
             * `ModalSheet` deliberately focuses the first real FIELD and skips
             * checkboxes ("focusing one makes space toggle it before the reader has
             * read the label"). Every control on this sheet IS a checkbox until the
             * reason picker, so the first field it finds is that picker's
             * Combobox input — and Headless UI opens a combobox's panel on focus.
             * The result, driven in a browser: the refund sheet opens with a list of
             * REASONS covering the plates the cashier came to pick, and the plate
             * list `inert` behind it. They have to dismiss a panel they never opened
             * before they can tick anything.
             *
             * `[data-autofocus]` is that behaviour's own documented override. The
             * plate list is the right target: focus lands inside the sheet (so the
             * trap and Escape work), the reader's eye starts where the decision is,
             * and nothing is armed — `tabIndex={-1}` makes it programmatically
             * focusable without adding a tab stop.
             */
            data-autofocus
            tabIndex={-1}
            className="flex max-h-[36svh] flex-col gap-1 overflow-y-auto outline-none"
            data-testid="refund-plate-list"
          >
            {plates.map((item) => {
              const share = shares?.get(item.id) ?? null;
              const checked = selected.has(item.id);
              /* R12/§10 — this plate is already out of the till. It stays ON
                 the list rather than disappearing, because a bill that silently
                 grew shorter between two openings of this sheet is how a
                 cashier loses track of what was given back; it is shown, said
                 in words, and not selectable. */
              const alreadyBack = item.refunded;
              return (
                <li key={item.id}>
                  <label
                    className={[
                      "flex items-start gap-2.5 rounded-xl border px-2.5 py-2 transition-colors",
                      alreadyBack
                        ? "cursor-not-allowed border-[color:var(--border-light)] bg-[color:var(--surface-alt)] opacity-60"
                        : checked
                          ? "cursor-pointer border-[color:var(--danger)] bg-[color:var(--danger-100,var(--surface-alt))]"
                          : "cursor-pointer border-[color:var(--border-light)]",
                    ].join(" ")}
                  >
                    <Checkbox
                      checked={checked && !alreadyBack}
                      disabled={alreadyBack}
                      onChange={() => toggle(item.id)}
                      data-testid={`refund-plate-${item.id}`}
                      aria-label={plateName(item)}
                    />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-sm font-semibold">
                          {plateName(item)}
                        </span>
                        <span className="shrink-0 text-sm font-extrabold tabular-nums">
                          {share ? money(share.amount) : money(item.lineTotal)}
                        </span>
                      </span>
                      {comboLabelForLocale(item, locale) ? (
                        <span className="truncate text-[length:var(--font-size-2xs)] text-[color:var(--text-muted)]">
                          {comboLabelForLocale(item, locale)}
                        </span>
                      ) : null}
                      {addonLabelForLocale(item, locale) ? (
                        <span className="truncate text-[length:var(--font-size-2xs)] text-[color:var(--text-muted)]">
                          {`+ ${addonLabelForLocale(item, locale)}`}
                        </span>
                      ) : null}
                      {/* The SWAP, on the same footing as the addons above it.
                          Without it a cashier ticking «موهيتو» to refund could
                          not see that this particular one went out with V7, and
                          the guest's slip named a drink they were never served.
                          Master's own label helper, in this sheet's language. */}
                      {substitutionLabelForLocale(item, locale, money) ? (
                        <span
                          className="truncate text-[length:var(--font-size-2xs)] text-[color:var(--text-muted)]"
                          data-testid={`refund-plate-swap-${item.id}`}
                        >
                          {substitutionLabelForLocale(item, locale, money)}
                        </span>
                      ) : null}
                      {/* In WORDS, not only in grey: opacity alone reads as
                          "loading" or "unavailable", and the cashier is being
                          told a specific fact — this one already went back. */}
                      {alreadyBack ? (
                        <span
                          className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-secondary)]"
                          data-testid={`refund-plate-back-${item.id}`}
                        >
                          {t((m) => m.pos.refundPlateAlreadyBack)}
                        </span>
                      ) : null}
                      {/* R3 ON THE ROW: what of this plate's refund is goods,
                          what is the service charge, what is VAT. The residue
                          between two identically priced plates is visible here
                          on purpose — the refund receipt prints the same
                          split, and the customer reads it. */}
                      {share ? (
                        /* NOT `dir="ltr"`: this line is Arabic labels with
                           Western figures between them, and `useMoney` already
                           pins the digits. Forcing LTR on a mixed run reorders
                           the Arabic, which is the bidi breakage the app rule
                           exists to prevent. The signed figures below keep it,
                           because a leading minus really is weak-directional. */
                        <span
                          className="text-[length:var(--font-size-2xs)] font-semibold tabular-nums text-[color:var(--text-muted)]"
                          data-testid={`refund-plate-split-${item.id}`}
                        >
                          {[
                            `${t((m) => m.invoices.netBeforeCharges)} ${money(share.netAmount)}`,
                            share.serviceChargeAmount > 0
                              ? `${t((m) => m.invoices.serviceCharge)} ${money(share.serviceChargeAmount)}`
                              : null,
                            share.vatAmount > 0
                              ? `${t((m) => m.invoices.vat)} ${money(share.vatAmount)}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        {/* D141 — under the plates it is about, above the fee it is not. */}
        {showReturnChoice ? (
          <ReturnToStockChoice
            returned={returned}
            onChange={setReturned}
            eligibleCount={returnableSelectedIds.length}
            selectedCount={selectedIds.length}
            testIdPrefix="refund"
          />
        ) : null}

        {/* THE COURIER'S FEE (F17), as its own tick BELOW the plates and above
            the total — because it is not a plate and must not read as one.
            Rendered only on a bill that carries a fee at all, so an eat-in sheet
            is unchanged.

            The founder's rule made a control: "the cashier's choice, per refund".
            Not a consequence of ticking every plate, which is what an inferred
            version would have been — and which would have meant two
            identical-looking refunds nobody could explain a month later. */}
        {feeOffered ? (
          <label
            className={[
              "flex items-center gap-2.5 rounded-xl px-3 py-2.5",
              feePickable
                ? "bg-[color:var(--surface-alt)]"
                : "bg-[color:var(--surface-alt)] opacity-60",
            ].join(" ")}
            data-testid="refund-fee-row"
          >
            <Checkbox
              checked={feePickable && feeSelected}
              disabled={!feePickable}
              onChange={() => setFeeSelected((current) => !current)}
              data-testid="refund-fee-checkbox"
              aria-label={t((m) => m.pos.refundDeliveryFee)}
            />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex items-baseline justify-between gap-2">
                <span className="truncate text-sm font-bold">
                  {t((m) => m.pos.refundDeliveryFee)}
                </span>
                <span className="shrink-0 text-sm font-extrabold tabular-nums">
                  {money(feeAmount)}
                </span>
              </span>
              {/* The FEE AND ITS VAT, named — the bill prints them as two lines
                  and this row is one, so the cashier has to be told which
                  number they are looking at. */}
              <span
                className="text-[length:var(--font-size-2xs)] font-semibold tabular-nums text-[color:var(--text-muted)]"
                data-testid="refund-fee-split"
              >
                {[
                  `${t((m) => m.invoices.deliveryFee)} ${money(invoice.deliveryFee)}`,
                  invoice.deliveryFeeVat > 0
                    ? `${t((m) => m.invoices.vat)} ${money(invoice.deliveryFeeVat)}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
              {/* In WORDS, like a plate that already came back: opacity alone
                  reads as "loading", and the cashier is being told a fact. */}
              {feeAlreadyBack ? (
                <span
                  className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-secondary)]"
                  data-testid="refund-fee-already-back"
                >
                  {t((m) => m.pos.refundDeliveryFeeAlreadyBack)}
                </span>
              ) : (
                <span className="text-[length:var(--font-size-2xs)] font-semibold leading-relaxed text-[color:var(--text-muted)]">
                  {t((m) => m.pos.refundDeliveryFeeHint)}
                </span>
              )}
            </span>
          </label>
        ) : null}

        {/* What is coming back, and what it is made of. */}
        <div
          className="flex flex-col gap-1 rounded-xl bg-[color:var(--surface-alt)] px-3 py-2.5"
          data-testid="refund-totals"
        >
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-bold">
              {t((m) => m.pos.refundBackToCustomer)}
            </span>
            {/* Danger red is a statement — "this much money is leaving" — so
                it belongs on an amount, not on the zero the sheet opens with.
                Colouring 0 red made the sheet greet the cashier with a red
                money figure that is not a refund and cannot become one until
                a plate is ticked. */}
            <span
              className={[
                "text-lg font-extrabold tabular-nums",
                backToCustomer > 0
                  ? "text-[color:var(--danger)]"
                  : "text-[color:var(--text-muted)]",
              ].join(" ")}
              data-testid="refund-total-amount"
            >
              {money(backToCustomer)}
            </span>
          </div>
          {selectedTotal ? (
            <div className="flex flex-col gap-0.5 tabular-nums">
              <SplitRow
                label={t((m) => m.invoices.netBeforeCharges)}
                value={money(selectedTotal.netAmount)}
              />
              {selectedTotal.serviceChargeAmount > 0 ? (
                <SplitRow
                  label={t((m) =>
                    invoice.pricesIncludeTax
                      ? m.invoices.serviceChargeIncluded
                      : m.invoices.serviceCharge,
                  )}
                  value={money(selectedTotal.serviceChargeAmount)}
                />
              ) : null}
              {selectedTotal.vatAmount > 0 ? (
                <SplitRow
                  label={t((m) =>
                    invoice.pricesIncludeTax
                      ? m.invoices.vatIncluded
                      : m.invoices.vat,
                  )}
                  value={money(selectedTotal.vatAmount)}
                />
              ) : null}
              {/* The fee on its OWN line in the split (F17), never folded into
                  net or VAT above: those two are the plates' allocation, and a
                  courier's fee is neither goods nor the tax on goods. */}
              {feeBack > 0 ? (
                <SplitRow
                  label={t((m) => m.pos.refundDeliveryFee)}
                  value={money(feeBack)}
                />
              ) : null}
            </div>
          ) : (
            /* The allocation could not be stated from this bill's own stored
               figures — the plates do not sum to its subtotal. The refund is
               still allowed (the SERVER allocates it, and it is the authority),
               but the till says it cannot show the split rather than quoting a
               number it cannot stand behind. */
            <span
              className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold leading-relaxed text-[color:var(--warning)]"
              data-testid="refund-split-unavailable"
            >
              {t((m) => m.pos.refundSplitUnavailable)}
            </span>
          )}
        </div>

        {/* WHAT IS LEFT ON THE CANVAS, and it is one sentence (UX-round D22).

            It explains why there is no amount field on a money sheet — an
            absence, which has no row to hide behind. The other five clauses,
            and this one's second half, are behind the door in the header. */}
        <p
          className="text-[length:var(--font-size-xs)] font-semibold leading-relaxed text-[color:var(--text-secondary)]"
          data-testid="refund-rules"
        >
          {t((m) => m.pos.refundRulePerPlateShort)}
        </p>

        <FormField
          name="refundReasonCode"
          label={t((m) => m.pos.refundReason)}
          error={
            canConfirmVoidReason(reasonCode)
              ? undefined
              : t((m) => m.pos.refundReasonRequired)
          }
        >
          <Combobox
            options={reasonCodesForKind("refund").map((code) => ({
              value: code,
              label: voidReasonLabel(t, code),
            }))}
            value={reasonCode}
            onChange={(next) => setReasonCode(next as ReasonCode | null)}
            onQueryChange={handleReasonQueryChange}
            placeholder={t((m) => m.pos.refundReasonPlaceholder)}
            aria-label={t((m) => m.pos.refundReason)}
            noResultsLabel={t((m) => m.common.empty)}
            data-testid="refund-reason-picker"
            // DEF-8: the panel is a viewport-anchored portal with no notion of
            // the footer below it, so it opens upward into the space the plate
            // list already occupies rather than over Confirm.
            optionsPlacement="top"
          />
        </FormField>

        <FormField
          name="refundReasonNote"
          // R10: the requirement is stated in the field's own label, so a
          // cashier who picks OTHER sees WHY Confirm stopped rather than
          // guessing at a disabled button.
          label={
            noteRequired
              ? `${t((m) => m.pos.voidReasonNoteLabel)} (${t((m) => m.common.required)})`
              : t((m) => m.pos.voidReasonNoteLabel)
          }
          error={
            noteRequired && reasonNote.trim().length === 0
              ? t((m) => m.pos.refundReasonNoteRequired)
              : undefined
          }
        >
          <TextInput
            name="refundReasonNote"
            value={reasonNote}
            onChange={(event) => setReasonNote(event.target.value)}
            placeholder={
              noteRequired
                ? t((m) => m.pos.voidReasonNoteRequiredPlaceholder)
                : t((m) => m.pos.voidReasonNoteOptionalPlaceholder)
            }
            maxLength={VOID_REASON_NOTE_MAX_LENGTH}
            dir="auto"
            data-testid="refund-reason-note"
          />
        </FormField>

      </div>
    </Modal>
    <BasisDisclosure
      openAtId={ruleDoorOpen ? "refund-rules" : null}
      onClose={() => setRuleDoorOpen(false)}
      title={t((m) => m.pos.refundRulesTitle)}
      closeLabel={t((m) => m.common.close)}
      sections={ruleSections}
      testId="refund-rules-sheet"
      elevated
    />
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="refund-discard-prompt"
    />
    </>
  );
}

/** One component of the refund, muted beside the figure it adds up to. */
function SplitRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
